import { ApplicationRef, Injectable, NgZone } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import {
  BehaviorSubject,
  concat,
  fromEvent,
  interval,
  Subscription,
  timer
} from 'rxjs';
import { filter, first, throttleTime } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class UpdatesService {
  public updatesSource = new BehaviorSubject<boolean>(false);
  public updatesCalled$ = this.updatesSource.asObservable();

  // How long a user can sit on one page (no navigation) before we surface the
  // manual refresh banner as a fallback. 5 minutes, in milliseconds.
  private static readonly GRACE_MS = 5 * 60 * 1000;

  private updatePending = false;
  private updateApplied = false;
  private graceTimerSub?: Subscription;

  constructor(
    private readonly _appRef: ApplicationRef,
    private readonly _swUpdate: SwUpdate,
    private readonly _zone: NgZone,
    private readonly _router: Router
  ) {
    if (!this._swUpdate.isEnabled) return;

    const everyTenMin$ = interval(10 * 60 * 1000);
    const appIsStable$ = this._appRef.isStable.pipe(
      first((isStable) => isStable)
    );
    const onFocus$ = fromEvent(document, 'visibilitychange').pipe(
      filter(() => document.visibilityState === 'visible'),
      throttleTime(60 * 1000)
    );

    this._zone.runOutsideAngular(() => {
      concat(appIsStable$, everyTenMin$).subscribe(() => this.safeCheck());
      onFocus$.subscribe(() => this.safeCheck());

      // Bridge DevTools "Update" / SW lifecycle into Angular's checkForUpdate.
      // Fires only when the SW script actually changes — usually no-op
      // between Angular deploys since ngsw-worker.js is byte-identical.
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.addEventListener('controllerchange', () =>
          this.safeCheck()
        );
        navigator.serviceWorker.getRegistration().then((reg) => {
          reg?.addEventListener('updatefound', () => this.safeCheck());
        });
      }
    });

    this._swUpdate.versionUpdates
      .pipe(
        filter(
          (event): event is VersionReadyEvent => event.type === 'VERSION_READY'
        )
      )
      .subscribe(() => this._zone.run(() => this.onUpdateReady()));

    // Apply a pending update silently on the next route change — the user is
    // leaving the current page anyway, so a full reload loses no work.
    this._router.events
      .pipe(filter((e): e is NavigationStart => e instanceof NavigationStart))
      .subscribe((e) => this.onNavigation(e.url));
  }

  public checkUpdates(): void {
    if (!this._swUpdate.isEnabled) return;

    this.safeCheck();
  }

  public applyUpdate(): void {
    if (!this._swUpdate.isEnabled) {
      document.location.reload();
      return;
    }

    this._swUpdate.activateUpdate().finally(() => document.location.reload());
  }

  private onUpdateReady(): void {
    if (this.updatePending) return;
    this.updatePending = true;

    // Fallback: if the user never navigates, surface the manual banner.
    this.graceTimerSub = timer(UpdatesService.GRACE_MS).subscribe(() =>
      this.updatesSource.next(true)
    );
  }

  private onNavigation(targetUrl: string): void {
    if (!this.updatePending || this.updateApplied) return;
    this.updateApplied = true;
    this.graceTimerSub?.unsubscribe();

    this._swUpdate
      .activateUpdate()
      .then(() => this.reloadTo(targetUrl))
      .catch(() => {
        // Activation failed — let the user retry via the manual banner.
        this.updateApplied = false;
        this.updatesSource.next(true);
      });
  }

  // Seam for testing: full browser load to the destination on the new version.
  protected reloadTo(url: string): void {
    location.assign(url);
  }

  private safeCheck(): void {
    this._swUpdate.checkForUpdate().catch(() => {
      /* next tick */
    });
  }
}
